import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo, useState } from 'react'

import { useTranslation } from '@root/shared/localization'
import { ChargeExactIcon, ConfirmDialog } from '@root/shared/ui-kit'

import { reatomDumpPosition } from '../atoms'

import { SellButton } from './sell-button'

import type {
  AggregatedPositionModel,
  WalletPositionModel,
} from '@root/entities/position'
import type { ClassNameProps } from '@root/shared/react'

const SellAllPositionsButton_: React.FC<
  {
    position: AggregatedPositionModel | WalletPositionModel
  } & ClassNameProps
> = ({ className, position }) => {
  const { t } = useTranslation()
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [remainingSizeCurrency] = useAtom(position.remainingSizeCurrency)

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomDumpPosition({ position }),
    [position],
  )
  const [requestState] = useAtom(model.requestState)
  const executeSell = useAction(model.executeSellAction)

  const handleClick = () => {
    setConfirmationOpen(true)
  }

  const handleSellAllPositions = () => {
    executeSell()
    setConfirmationOpen(false)
  }

  return (
    <>
      <SellButton
        disabled={remainingSizeCurrency.isZero()}
        className={className}
        onClick={handleClick}
      >
        <ChargeExactIcon width={8} />
        {t('trading.sell-all')}
      </SellButton>
      <ConfirmDialog
        describedBy={t('trading.sell')}
        title={t('trading.sell-all')}
        intent="error"
        description={t('trading.sell-all-positions-description', {
          ticker: position.pair.quote.ticker,
        })}
        confirmLabel={t('common.yes')}
        open={confirmationOpen}
        onOpenChange={() => setConfirmationOpen(false)}
        onCancel={() => setConfirmationOpen(false)}
        onConfirm={handleSellAllPositions}
        busy={requestState.type === 'Loading'}
      />
    </>
  )
}

type SellAllPositionsButtonProps = {
  position: AggregatedPositionModel | WalletPositionModel | undefined
} & ClassNameProps

export const SellAllPositionsButton: React.FC<SellAllPositionsButtonProps> = ({
  className,
  position,
}) => {
  const { t } = useTranslation()

  if (!position) {
    return (
      <SellButton disabled={true} className={className} onClick={() => {}}>
        <ChargeExactIcon width={8} />
        {t('trading.sell-all')}
      </SellButton>
    )
  }

  return <SellAllPositionsButton_ position={position} className={className} />
}
